import { MultiSelect } from "../../forms/components/MultiSelect";
import { useForm } from "react-hook-form";
import { Key } from "../../shared/models/Key";
import { WingmanAdminDocument } from "../models/WingmanAdminDocument";
import { canEdit } from "../utils/helpers";
import { Form } from "../../forms/components/Form";
import { t } from "i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormRow } from "../../forms/components/FormRow";
import { role_options as options } from "../utils/helpers";

const WingmanAdminRoleSelect = ({
  roles,
  handleSelectedRoles,
  fileName,
  id,
  uploadStatus,
  authorizedBrands,
  brands,
}: {
  roles: string[];
  handleSelectedRoles: (payload: WingmanAdminDocument) => void;
  fileName: string;
  id: number;
  uploadStatus: string;
  authorizedBrands: string[];
  brands: string[];
}) => {
  const userCanEdit = canEdit(authorizedBrands, brands);
  const disabled = uploadStatus === "Complete" && !userCanEdit;

  const schema = z.object({
    roles: z.array(z.string()).optional(),
  });
  type Schema = z.infer<typeof schema>;

  const defaultRoles = roles
    .map((role) => role.trim())
    .filter((role) => options.some((option) => option.id === role));

  // If no preselected role => show empty selection
  const defaultValues: Schema = {
    roles: roles.length === 0 ? [] : defaultRoles,
  };

  const methods = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const { register } = methods;

  const displayText = (
    data: Key<string | number>[],
    value?: (string | number | undefined)[]
  ): string => {
    // If nothing is selected, show placeholder
    if (!value || value.length === 0) {
      return t`Select Roles`;
    }

    // Check if the user selected all roles
    const isAllRolesSelected =
      value.length === data.length &&
      value.every((id) => data.some((role) => role.id === id));

    if (isAllRolesSelected) {
      return t`All Roles`;
    }

    // Otherwise, display a comma-separated list of the selected roles
    const selectedOptions = data.filter((option) => value.includes(option.id));
    return selectedOptions.map((option) => option.name).join(", ");
  };

  const handleBlurSubmit = () => {
    const roles = methods.getValues("roles") ?? [];
    if (!roles) return;

    handleSelectedRoles({
      id,
      Filename: fileName,
      Role: roles,
      uploadStatus: uploadStatus,
    });
  };

  const handleSubmit = () => {
    // NOOP (just required for the <Form> submission)
  };

  return (
    <Form {...methods} onSubmit={handleSubmit}>
      <FormRow>
        <MultiSelect
          label=""
          onBlurSubmit={handleBlurSubmit}
          {...register("roles")}
          displayText={displayText}
          disabled={disabled}
          data={options}
        />
      </FormRow>
    </Form>
  );
};

export default WingmanAdminRoleSelect;

